import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  api,
  type JobField,
  type JobSpec,
  type JobStatus,
  type SeriesCoverage,
} from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  type Choice,
  EmptyBox,
  KeyValue,
  Pagination,
  QueryBoundary,
  SearchSelect,
  Table,
  Td,
  Th,
} from '../components/ui';
import { atLeast } from '../lib/roles';
import { ago } from '../lib/format';

/** Compact duration: 45s, 3m 20s, 1h 4m. */
export function secs(total: number): string {
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  if (m < 60) return `${m}m ${total % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

export function jobTone(status: JobStatus) {
  return status === 'succeeded'
    ? 'good'
    : status === 'failed'
      ? 'bad'
      : status === 'cancelled'
        ? 'neutral'
        : status === 'running'
          ? 'info'
          : 'warn';
}

function defaultsFor(spec: JobSpec): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of spec.fields) {
    out[f.name] = f.type === 'bool' ? '' : f.default != null ? String(f.default) : '';
  }
  return out;
}

/** The account whose symbols a `symbol` field should offer, if any.
 *
 *  Only jobs that talk to the broker have an account field. The rest — Compute
 *  features, Build dataset, Data quality — read **stored candles**, and for
 *  those the right list is what has actually been imported, not the 7391
 *  symbols the broker offers. */
function accountIdOf(spec: JobSpec, values: Record<string, string>): number | null {
  const f = spec.fields.find((x) => x.type === 'account');
  if (!f) return null;
  const n = Number(values[f.name] ?? f.default ?? '');
  return Number.isFinite(n) && n > 0 ? n : null;
}

const day = (iso: string | null) => (iso ? iso.slice(0, 10) : '');

/** Pick a symbol from what the candle store holds, and say how much it holds. */
function StoredSymbolField({
  value,
  onChange,
  coverage,
  loading,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  coverage: SeriesCoverage[];
  loading: boolean;
  /** Set when the coverage request itself failed. Kept separate from "the
   *  store is empty": the first time this shipped, a 500 from a half-defined
   *  response model was displayed as "nothing imported yet" to someone who had
   *  just imported two years of data. An error must never read as an answer. */
  error: unknown;
}) {
  // One entry per symbol, with every timeframe it has, so the list reads as
  // "XAUUSD — M1, M5" rather than repeating the symbol once per timeframe.
  const bySymbol = new Map<string, SeriesCoverage[]>();
  for (const c of coverage) bySymbol.set(c.symbol, [...(bySymbol.get(c.symbol) ?? []), c]);

  const choices: Choice[] = [...bySymbol.entries()].map(([symbol, rows]) => ({
    value: symbol,
    label: symbol,
    hint: rows
      .map((r) => `${r.timeframe}: ${r.bars.toLocaleString()} bar`)
      .join(' · '),
  }));

  return (
    <div className="space-y-1">
      <SearchSelect
        value={value}
        choices={choices}
        onChange={onChange}
        loading={loading}
        placeholder="Search imported symbols…"
        emptyLabel={error ? 'could not load the list' : 'nothing imported yet'}
      />
      {error ? (
        <p className="text-xs text-red-400">
          Could not read what has been imported: {String(error)}. This is not the same as
          an empty store — the list may well have entries.
        </p>
      ) : (
        !loading &&
        choices.length === 0 && (
          <p className="text-xs text-amber-500">
            The candle store is empty. Import history is the first job in the sequence.
          </p>
        )
      )}
    </div>
  );
}

/** Several stored symbols at once, as a comma-separated value.
 *
 *  Build dataset is the one job that needs this: §13.4 rejects a model whose
 *  out-of-sample profit is more than 60% from a single symbol, so a dataset
 *  built from one symbol can only ever produce REJECTED models. A single-value
 *  picker made the dead end the default, and the dead end took a full training
 *  run to discover.
 */
function StoredSymbolsField({
  value,
  onChange,
  coverage,
  loading,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  coverage: SeriesCoverage[];
  loading: boolean;
  error: unknown;
}) {
  const selected = value ? value.split(',').filter(Boolean) : [];
  const bySymbol = new Map<string, SeriesCoverage[]>();
  for (const c of coverage) bySymbol.set(c.symbol, [...(bySymbol.get(c.symbol) ?? []), c]);

  const toggle = (symbol: string) => {
    const next = selected.includes(symbol)
      ? selected.filter((s) => s !== symbol)
      : [...selected, symbol];
    onChange(next.join(','));
  };

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap gap-1.5">
        {[...bySymbol.entries()].map(([symbol, rows]) => {
          const on = selected.includes(symbol);
          return (
            <button
              key={symbol}
              type="button"
              onClick={() => toggle(symbol)}
              title={rows.map((r) => `${r.timeframe}: ${r.bars.toLocaleString()} bars`).join(' · ')}
              className={`rounded border px-2 py-1 text-xs ${
                on ? 'border-sky-500 bg-sky-500/10 text-sky-300' : 'border-edge text-slate-400'
              }`}
            >
              {symbol}
            </button>
          );
        })}
      </div>
      {error ? (
        <p className="text-xs text-red-400">
          Could not read what has been imported: {String(error)}. This is not the same as
          an empty store — the list may well have entries.
        </p>
      ) : loading ? (
        <p className="text-xs text-slate-600">loading imported symbols…</p>
      ) : bySymbol.size === 0 ? (
        <p className="text-xs text-amber-500">
          The candle store is empty. Import history is the first job in the sequence.
        </p>
      ) : selected.length === 1 ? (
        <p className="text-xs text-amber-500">
          One symbol only. §13.4 rejects a model whose out-of-sample profit is more than 60%
          from a single symbol, so nothing trained on this dataset can be approved.
        </p>
      ) : null}
    </div>
  );
}

function AccountField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: api.accounts });
  const choices: Choice[] = (accounts.data?.items ?? []).map((a) => ({
    value: String(a.id),
    label: a.name,
    hint: `#${a.id} · ${a.broker ?? 'no broker'} · login ${a.login ?? '—'} · ${a.trading_mode}`,
  }));
  return (
    <SearchSelect
      value={value}
      choices={choices}
      onChange={onChange}
      loading={accounts.isLoading}
      placeholder="Search accounts…"
      emptyLabel="no accounts"
    />
  );
}

/**
 * Pick a symbol from what the broker actually offers.
 *
 * Typing a name by hand is how you get
 * "canonical symbol 'XAUUSD' has no broker mapping for this account" from a
 * job that has already been queued. So: the list comes from the account, and
 * an unmapped symbol is both marked and fixable in place.
 */
function SymbolField({
  accountId,
  value,
  onChange,
}: {
  accountId: number | null;
  value: string;
  onChange: (v: string) => void;
}) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const symbols = useQuery({
    queryKey: ['account-symbols', accountId, q],
    queryFn: () => api.accountSymbols(accountId as number, q),
    enabled: accountId != null,
  });

  const map = useMutation({
    mutationFn: (s: { symbol: string; broker_symbol: string }) =>
      api.mapAccountSymbol(accountId as number, s),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['account-symbols', accountId] }),
  });

  const items = symbols.data?.items ?? [];
  const choices: Choice[] = items.map((s) => ({
    value: s.symbol,
    label: s.symbol,
    hint: s.broker_symbol !== s.symbol ? `broker: ${s.broker_symbol}` : undefined,
    tag: s.is_mapped ? undefined : 'not mapped',
  }));
  const chosen = items.find((s) => s.symbol === value);

  if (accountId == null) {
    return <p className="text-xs text-slate-500">Pick an account first.</p>;
  }

  return (
    <div className="space-y-1">
      <SearchSelect
        value={value}
        choices={choices}
        onChange={onChange}
        onSearch={setQ}
        loading={symbols.isLoading || symbols.isFetching}
        placeholder="Search symbols…"
        emptyLabel={q ? 'nothing matches' : 'no symbols'}
      />
      {symbols.data?.gateway_detail && (
        <p className="text-xs text-amber-500">{symbols.data.gateway_detail}</p>
      )}
      {symbols.data && symbols.data.total > items.length && (
        <p className="text-xs text-slate-600">
          showing {items.length} of {symbols.data.total} — type to narrow
        </p>
      )}
      {chosen && !chosen.is_mapped && (
        <div className="flex items-center gap-2 text-xs text-amber-400">
          <span>not mapped — a job would fail on it</span>
          <Button
            size="sm"
            variant="ghost"
            disabled={map.isPending}
            onClick={() => map.mutate({ symbol: chosen.symbol, broker_symbol: chosen.broker_symbol })}
          >
            {map.isPending ? 'mapping…' : 'Map it'}
          </Button>
        </div>
      )}
      {map.isError && <p className="text-xs text-red-400">{String(map.error)}</p>}
    </div>
  );
}

function Field({
  field,
  value,
  onChange,
  datasetOptions,
  accountId,
  coverage,
  coverageLoading,
  coverageError,
  series,
}: {
  field: JobField;
  value: string;
  onChange: (v: string) => void;
  datasetOptions: { path: string; label: string }[];
  accountId: number | null;
  coverage: SeriesCoverage[];
  coverageLoading: boolean;
  coverageError: unknown;
  /** The stored series matching the symbol+timeframe chosen so far, if any. */
  series: SeriesCoverage | null;
}) {
  const base =
    'w-full rounded border border-edge bg-surface px-2 py-1.5 text-sm outline-none focus:border-slate-500';
  const label = (
    <span className="text-xs text-slate-400">
      {field.label}
      {field.required && <span className="text-red-400"> *</span>}
    </span>
  );

  if (field.type === 'bool') {
    return (
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" checked={value === 'true'} onChange={(e) => onChange(String(e.target.checked))} />
        {field.label}
      </label>
    );
  }

  if (field.type === 'symbols') {
    return (
      <label className="block space-y-1">
        {label}
        <StoredSymbolsField
          value={value}
          onChange={onChange}
          coverage={coverage}
          loading={coverageLoading}
          error={coverageError}
        />
        {field.help && <span className="text-xs text-slate-600">{field.help}</span>}
      </label>
    );
  }

  if (field.type === 'account' || field.type === 'symbol') {
    // A job with an account field talks to the broker, so it offers the
    // broker's symbols. Everything else reads stored candles, so it offers
    // what has been imported — and says how much of it there is.
    const usesBroker = accountId != null;
    return (
      <label className="block space-y-1">
        {label}
        {field.type === 'account' ? (
          <AccountField value={value} onChange={onChange} />
        ) : usesBroker ? (
          <SymbolField accountId={accountId} value={value} onChange={onChange} />
        ) : (
          <StoredSymbolField
            value={value}
            onChange={onChange}
            coverage={coverage}
            loading={coverageLoading}
            error={coverageError}
          />
        )}
        {field.help && <span className="text-xs text-slate-600">{field.help}</span>}
      </label>
    );
  }

  if (field.type === 'date') {
    const min = day(series?.first_bar_utc ?? null);
    const max = day(series?.last_bar_utc ?? null);
    return (
      <label className="block space-y-1">
        {label}
        <input
          type="date"
          value={value}
          min={min || undefined}
          max={max || undefined}
          onChange={(e) => onChange(e.target.value)}
          className={base}
        />
        {series ? (
          <span className="text-xs text-slate-500">
            Mavjud tarix: <span className="text-slate-300">{min}</span> —{' '}
            <span className="text-slate-300">{max}</span> ({series.bars.toLocaleString()} bar,{' '}
            {series.timeframe})
          </span>
        ) : (
          <span className="text-xs text-slate-600">
            Simvol va timeframe tanlangach mavjud sanalar ko'rsatiladi.
          </span>
        )}
      </label>
    );
  }

  if (field.type === 'select' || field.type === 'dataset') {
    const options =
      field.type === 'dataset'
        ? datasetOptions.map((d) => ({ value: d.path, label: d.label }))
        : field.choices.map((c) => ({ value: c, label: c }));
    return (
      <label className="block space-y-1">
        {label}
        <select value={value} onChange={(e) => onChange(e.target.value)} className={base}>
          <option value="">—</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {field.help && <span className="text-xs text-slate-600">{field.help}</span>}
      </label>
    );
  }

  return (
    <label className="block space-y-1">
      {label}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.help}
        className={base}
      />
      {field.help && <span className="text-xs text-slate-600">{field.help}</span>}
    </label>
  );
}

function JobForm({ spec, onDone }: { spec: JobSpec; onDone: () => void }) {
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>(() => defaultsFor(spec));
  const datasets = useQuery({
    queryKey: ['job-datasets'],
    queryFn: api.jobDatasets,
    enabled: spec.fields.some((f) => f.type === 'dataset'),
  });

  const accountId = accountIdOf(spec, values);
  // Fetched whenever the form has a symbol or a date: the symbol picker needs
  // it when the job reads stored candles, and the date inputs need it to bound
  // themselves to a range that actually has bars.
  const needsCoverage = spec.fields.some(
    (f) => f.type === 'symbol' || f.type === 'symbols' || f.type === 'date',
  );
  const coverage = useQuery({
    queryKey: ['coverage'],
    queryFn: api.coverage,
    enabled: needsCoverage,
  });

  const symbolField = spec.fields.find((f) => f.type === 'symbol' || f.type === 'symbols');
  const tfField = spec.fields.find((f) => f.name === 'timeframe');
  // For a multi-select, bound the date inputs by the first symbol chosen — the
  // range shown is indicative, and the job reads each symbol's own coverage.
  const chosenSymbol = symbolField ? (values[symbolField.name] ?? '').split(',')[0] : '';
  const chosenTf = tfField ? (values[tfField.name] ?? '') : '';
  const series =
    (coverage.data?.items ?? []).find(
      (c) => c.symbol === chosenSymbol && (!chosenTf || c.timeframe === chosenTf),
    ) ?? null;

  const run = useMutation({
    mutationFn: () => {
      const params: Record<string, unknown> = {};
      for (const f of spec.fields) {
        const raw = values[f.name] ?? '';
        if (f.type === 'bool') params[f.name] = raw === 'true';
        else if (raw !== '') params[f.name] = raw;
      }
      return api.createJob(spec.kind, params);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jobs'] });
      onDone();
    },
  });

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        {spec.fields.map((f) => (
          <Field
            key={f.name}
            field={f}
            value={values[f.name] ?? ''}
            onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))}
            datasetOptions={datasets.data ?? []}
            accountId={accountId}
            coverage={coverage.data?.items ?? []}
            coverageLoading={coverage.isLoading}
            coverageError={coverage.error}
            series={series}
          />
        ))}
      </div>
      {run.isError && (
        <p className="text-xs text-red-400">
          {run.error instanceof Error ? run.error.message : 'could not start'}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button variant="primary" size="sm" disabled={run.isPending} onClick={() => run.mutate()}>
          {run.isPending ? 'Starting…' : `Run (~${spec.est_minutes} min)`}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** Ketma-ketlik va har bir ishning maqsadi, o'zbekcha.
 *
 *  Bu sahifada o'nta ish bor va ular bir-biriga bog'liq: har biri oldingisining
 *  natijasidan o'qiydi. Tartibni bilmasdan bosilgan tugma xatolik bermaydi —
 *  shunchaki bo'sh yoki yarim natija beradi, bu esa ancha qimmatroq. Shuning
 *  uchun yo'riqnoma shu yerda, ishlar ro'yxatining tepasida turadi.
 */
function Guide() {
  const [open, setOpen] = useState(false);
  const steps: Array<{ n: string; title: string; body: string }> = [
    {
      n: '1',
      title: 'Import history — tarixni yuklash',
      body:
        'Brokerdagi shamlarni (candle) bazaga ko\'chiradi. Hamma narsa shundan boshlanadi. ' +
        'Gateway ishlab turishi va simvol hisobga bog\'langan bo\'lishi kerak. ' +
        'Ikki yillik M1 uchun ~750 ming bar keladi, ish uzoq ketadi.',
    },
    {
      n: '2',
      title: 'Data quality report — sifat hisoboti',
      body:
        'Nima kelganini tekshiradi: qamrov, bo\'shliqlar, qayta ko\'rilgan barlar. ' +
        'Bu bir daqiqalik ish, lekin keyingi hammasini qutqaradi — bo\'shliqli ' +
        'ma\'lumot ustiga o\'rgatilgan model o\'rganmaganini o\'rgangandek ko\'rinadi. ' +
        'Bo\'shliq chiqsa, o\'sha oraliqni qayta import qiling.',
    },
    {
      n: '3',
      title: 'Compute features — indikatorlar',
      body:
        'Saqlangan shamlar ustidan indikator matritsasini hisoblaydi (harakatlanuvchi ' +
        'o\'rtachalar, volatillik, momentum va h.k.). Model aynan shu ustunlardan o\'rganadi.',
    },
    {
      n: '4',
      title: 'Compute regimes — bozor rejimlari',
      body:
        'Har bir barga bozor holatini belgilaydi: trend, yon harakat, yuqori volatillik. ' +
        'Strategiya va model qaysi sharoitda ishlayotganini shu orqali ajratadi.',
    },
    {
      n: '5',
      title: 'Run strategy — nomzodlarni topish',
      body:
        'Tanlangan strategiyani saqlangan shamlar ustidan yurgizadi va uning kirish ' +
        'nuqtalarini — "nomzodlar"ni — yozib qo\'yadi. Keyingi qadam aynan shu ' +
        'nomzodlarga yorliq qo\'yadi, shuning uchun busiz Build dataset ' +
        '"no candidates" deb to\'xtaydi. Strategiya va oraliq ikkalasida bir xil bo\'lsin.',
    },
    {
      n: '6',
      title: 'Build dataset — o\'quv to\'plami',
      body:
        'Triple-barrier usulida yorliq qo\'yadi: har bir kirish nuqtasidan keyin narx avval ' +
        'take-profit ga yetdimi, stop-loss ga yetdimi, yoki vaqt tugadimi. Natija — ' +
        'dataset.json artefakti. Sana oralig\'ini import qilganingizdan bir oz TOR oling: ' +
        'indikatorlar boshida bir necha barni "isitishga" yeydi, yorliq esa oxirida ' +
        'max_holding barga oldinga qaraydi.',
    },
    {
      n: '7',
      title: 'Train models — modellarni o\'rgatish',
      body:
        'Purged/embargoed walk-forward bilan o\'rgatadi — ya\'ni model hech qachon kelajakni ' +
        'ko\'rmaydi, va o\'quv bilan test oralig\'i orasida bo\'shliq qoldiriladi. ' +
        'Har bir algoritm natijasi registrga VALIDATED yoki REJECTED bo\'lib tushadi. ' +
        'Eng uzoq ish — soatlab ketishi mumkin.',
    },
    {
      n: '8',
      title: 'Backtest — qayta o\'ynatish',
      body:
        'Bitta strategiyani sana oralig\'ida deterministik qayta o\'ynatadi. ' +
        'Bir xil kirish — har doim bir xil natija (INV-8), shuning uchun taqqoslash mumkin.',
    },
    {
      n: '9',
      title: 'Pre-flight check — savdo oldidan tekshiruv',
      body:
        'Savdodan oldingi oxirgi to\'siq: gateway ulanganmi, hisob to\'g\'rimi, risk ' +
        'chegaralari joyidami, ma\'lumot yangimi. Exit 0 = tayyor, 1 = to\'xtatildi.',
    },
    {
      n: '10',
      title: 'Daily verification — kunlik solishtirish',
      body:
        'Brokerdagi pozitsiyalar bilan bazadagi yozuvlar mos kelishini, osilib qolgan ' +
        'buyruq yo\'qligini va equity egri chizig\'i uzluksizligini tekshiradi. Har kuni.',
    },
  ];

  return (
    <Card className="mb-6 p-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="text-sm font-medium text-slate-300">
          Yo'riqnoma — ishlar ketma-ketligi va ular nima qiladi
        </span>
        <span className="text-slate-500">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="mt-4 space-y-4 text-sm">
          <p className="text-slate-400">
            Ishlar bir-biriga bog'liq: har biri oldingisining natijasidan o'qiydi. Tartibni
            buzib bosilgan tugma xato bermaydi — bo'sh yoki yarim natija beradi, bu esa
            qimmatroq. Tartib bilan yuring.
          </p>

          <ol className="space-y-3">
            {steps.map((s) => (
              <li key={s.n} className="flex gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-800 text-xs text-slate-300">
                  {s.n}
                </span>
                <div className="min-w-0">
                  <div className="text-slate-200">{s.title}</div>
                  <p className="mt-0.5 text-slate-500">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="rounded border border-edge bg-surface/50 p-3">
            <div className="mb-1 text-slate-300">Bilib qo'yish foydali</div>
            <ul className="list-disc space-y-1 pl-5 text-slate-500">
              <li>
                <span className="text-slate-400">Simvol ro'yxati ikki xil.</span> Brokerga
                boradigan ishlarda (Import history, Pre-flight) brokerning simvollari
                ko'rsatiladi. Qolganlarida — bazada haqiqatan mavjud bo'lganlari.
              </li>
              <li>
                <span className="text-slate-400">Sanalar cheklangan.</span> Simvol va
                timeframe tanlangach, mavjud tarix oralig'i ko'rsatiladi va sana maydonlari
                o'sha oraliqdan tashqariga chiqmaydi.
              </li>
              <li>
                <span className="text-slate-400">"not mapped" belgisi.</span> Broker taklif
                qiladigan, lekin hisobga bog'lanmagan simvol shunday belgilanadi. Yonidagi
                "Map it" tugmasi buni tuzatadi — busiz ish xato bilan to'xtaydi.
              </li>
              <li>
                <span className="text-slate-400">Ishlar to'xtatilmaydi.</span> Har biri
                worker'da alohida jarayon sifatida ketadi, chiqishi shu yerga oqib turadi.
                Sahifani yopsangiz ham ish davom etadi.
              </li>
            </ul>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Operations() {
  const { user } = useAuth();
  const canRun = atLeast(user?.role, 'operator');
  const [openKind, setOpenKind] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const catalog = useQuery({ queryKey: ['job-catalog'], queryFn: api.jobCatalog });
  const health = useQuery({ queryKey: ['health'], queryFn: api.health });
  const worker = useQuery({
    queryKey: ['worker-status'],
    queryFn: api.workerStatus,
    refetchInterval: 15_000,
  });

  const jobs = useQuery({
    queryKey: ['jobs', offset],
    queryFn: () => api.jobs({ limit: 25, offset }),
    refetchInterval: (q) =>
      (q.state.data?.items ?? []).some((j) => j.status === 'queued' || j.status === 'running')
        ? 3000
        : 15000,
  });

  const specs = useMemo(() => catalog.data ?? [], [catalog.data]);

  return (
    <>
      <PageHeader
        title="Operations"
        description="Run the research → demo pipeline. Each job runs as a subprocess in the worker; its output streams back here."
      />

      <Guide />

      {health.data && (
        <Card className="mb-6 p-4">
          <h2 className="mb-3 text-sm font-medium text-slate-300">System health</h2>
          <KeyValue
            rows={[
              ['Status', health.data.status],
              ['Version', health.data.version],
              ['Environment', health.data.environment],
              ...Object.entries(health.data.components).map(
                ([name, c]) =>
                  [
                    name,
                    <span className={c.status === 'ok' ? 'text-emerald-400' : 'text-red-400'}>
                      {c.status}
                      {c.latency_ms != null && ` · ${c.latency_ms} ms`}
                      {c.detail && ` · ${c.detail}`}
                    </span>,
                  ] as [string, ReactNode],
              ),
            ]}
          />
        </Card>
      )}

      <h2 className="mb-2 text-sm font-semibold text-slate-300">Run a job</h2>
      {!canRun && (
        <p className="mb-3 text-xs text-slate-500">Launching jobs requires the operator role.</p>
      )}
      <QueryBoundary q={catalog}>
        <div className="mb-8 grid gap-3 md:grid-cols-2">
          {specs.map((spec) => (
            <Card key={spec.kind} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-slate-200">{spec.label}</div>
                    {spec.danger && (
                      <span className="rounded border border-red-900 bg-red-950 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-300">
                        Changes trading
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{spec.description}</p>
                </div>
                {canRun && openKind !== spec.kind && (
                  <Button size="sm" onClick={() => setOpenKind(spec.kind)}>
                    Configure
                  </Button>
                )}
              </div>
              {openKind === spec.kind && (
                <div className="mt-4 border-t border-edge pt-4">
                  <JobForm spec={spec} onDone={() => setOpenKind(null)} />
                </div>
              )}
            </Card>
          ))}
        </div>
      </QueryBoundary>

      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-300">Recent runs</h2>
        {worker.data && (
          <span
            className={`text-xs ${worker.data.online ? 'text-emerald-400' : 'text-red-400'}`}
            title={worker.data.workers.join(', ')}
          >
            {worker.data.online ? '● worker online' : '● NO WORKER'} — {worker.data.detail}
          </span>
        )}
      </div>

      {worker.data && !worker.data.online && (
        <Card className="mb-3 border-red-900 bg-red-950/40 px-4 py-3 text-sm text-red-200">
          Nothing is consuming the queue, so a queued job will not start. Check the{' '}
          <code>worker</code> container.
        </Card>
      )}
      <QueryBoundary q={jobs} isEmpty={jobs.data?.items.length === 0} empty={<EmptyBox>No jobs run yet.</EmptyBox>}>
        {jobs.data && (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Job</Th>
                  <Th>Kind</Th>
                  <Th>Status</Th>
                  <Th>Started</Th>
                  <Th>By</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {jobs.data.items.map((j) => (
                  <tr key={j.id} className="hover:bg-slate-800/30">
                    <Td className="tabular-nums">
                      <Link className="text-sky-400 hover:underline" to={`/operations/jobs/${j.id}`}>
                        #{j.id}
                      </Link>
                    </Td>
                    <Td>{j.kind}</Td>
                    <Td>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge tone={jobTone(j.status)}>{j.status}</Badge>
                        {j.exit_code != null && j.exit_code !== 0 && (
                          <span className="text-xs text-red-400">exit {j.exit_code}</span>
                        )}
                        {j.status === 'running' && j.running_seconds != null && (
                          <span className="text-xs text-sky-400">{secs(j.running_seconds)}</span>
                        )}
                        {j.status === 'queued' && j.waiting_seconds != null && (
                          <span
                            className={`text-xs ${
                              j.waiting_seconds > 60 || worker.data?.online === false
                                ? 'text-red-400'
                                : 'text-slate-500'
                            }`}
                          >
                            waiting {secs(j.waiting_seconds)}
                            {worker.data?.online === false
                              ? ' — no worker'
                              : j.waiting_seconds > 60
                                ? ' — stuck?'
                                : ''}
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap text-xs text-slate-400">
                      {j.started_at ? ago(j.started_at) : '—'}
                    </Td>
                    <Td className="text-xs text-slate-500">{j.created_by_email ?? '—'}</Td>
                    <Td>
                      <Link className="text-xs text-sky-400 hover:underline" to={`/operations/jobs/${j.id}`}>
                        output →
                      </Link>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination total={jobs.data.total} limit={25} offset={offset} onChange={setOffset} />
          </>
        )}
      </QueryBoundary>

      <p className="mt-6 text-xs text-slate-500">
        MLflow (training runs, metrics, artifacts) runs on the <code>mlflow</code> service, port 5000.
        Emergency stop and halt-clear are on the{' '}
        <Link className="text-sky-400 hover:underline" to="/accounts">
          Accounts
        </Link>{' '}
        page.
      </p>
    </>
  );
}
